import React from "react";
import { motion } from "framer-motion";

import {
  FaFacebookF,
  FaEnvelope,
  FaQuoteLeft,
  FaTools,
  FaBriefcase,
  FaLightbulb,
  FaGraduationCap,
  FaCheckCircle,
  FaArrowRight,
} from "react-icons/fa";

import ceoImage from "./igniteboss.jpg";
import Footer from "./Footer";

const PrincipalMessagePage = () => {
  return (
    <div className="ignite-ceo-page">
      <style>{`
        /* =========================================================
           IGNITE S VOCATIONAL SCHOOL
           CEO MESSAGE PAGE
           GREEN + BLACK + YELLOW + WHITE + RED ACCENT
        ========================================================= */

        :root {
          --ignite-green: #087f23;
          --ignite-green-dark: #056219;
          --ignite-green-deep: #034d13;
          --ignite-green-soft: #edf8f0;

          --ignite-yellow: #f4c400;
          --ignite-yellow-light: #ffda32;
          --ignite-yellow-dark: #d8a900;

          --ignite-red: #d90429;
          --ignite-red-dark: #b80020;

          --ignite-black: #090909;
          --ignite-dark: #111111;
          --ignite-text: #202020;

          --ignite-gray: #687076;
          --ignite-gray-light: #f5f7f6;

          --ignite-white: #ffffff;

          --ignite-border: #e6e9e7;

          --ignite-shadow:
            0 20px 60px rgba(0, 0, 0, 0.10);

          --ignite-radius: 20px;
        }

        .ignite-ceo-page {
          width: 100%;
          min-height: 100vh;

          overflow: hidden;

          background:
            #ffffff;

          color:
            var(--ignite-text);

          font-family:
            "Montserrat",
            "Poppins",
            "Segoe UI",
            Arial,
            sans-serif;
        }

        .ignite-ceo-page *,
        .ignite-ceo-page *::before,
        .ignite-ceo-page *::after {
          box-sizing: border-box;
        }

        /* =========================================================
           HERO
        ========================================================= */

        .ceo-hero {
          position: relative;

          width: 100%;

          min-height: 390px;

          display: flex;
          align-items: center;

          padding:
            90px 30px 80px;

          overflow: hidden;

          background:
            linear-gradient(
              120deg,
              #034d13 0%,
              #087f23 52%,
              #075d1b 100%
            );
        }

        .ceo-hero::before {
          content: "";

          position: absolute;

          width: 520px;
          height: 520px;

          right: -160px;
          top: -240px;

          border-radius: 50%;

          background:
            rgba(244, 196, 0, 0.13);
        }

        .ceo-hero::after {
          content: "";

          position: absolute;

          width: 350px;
          height: 350px;

          left: -160px;
          bottom: -210px;

          border-radius: 50%;

          border:
            70px solid
            rgba(255, 255, 255, 0.05);
        }

        .ceo-hero-inner {
          position: relative;

          z-index: 2;

          width:
            min(1200px, 100%);

          margin: 0 auto;

          text-align: center;
        }

        .ceo-hero-label {
          display: inline-flex;

          align-items: center;

          gap: 9px;

          padding:
            9px 16px;

          margin-bottom: 18px;

          color:
            var(--ignite-black);

          background:
            var(--ignite-yellow);

          border-radius: 50px;

          font-size: 10px;

          font-weight: 800;

          text-transform: uppercase;

          letter-spacing: 1.8px;
        }

        .ceo-hero-label svg {
          font-size: 13px;
        }

        .ceo-hero h1 {
          margin: 0;

          color:
            var(--ignite-white);

          font-size:
            clamp(34px, 5vw, 62px);

          font-weight: 900;

          line-height: 1.04;

          letter-spacing: -1.5px;
        }

        .ceo-hero h1 span {
          color:
            var(--ignite-yellow);
        }

        .ceo-hero-description {
          width:
            min(700px, 100%);

          margin:
            20px auto 0;

          color:
            rgba(255, 255, 255, 0.82);

          font-size:
            clamp(14px, 2vw, 17px);

          line-height: 1.8;
        }

        .hero-accent {
          width: 80px;
          height: 5px;

          margin:
            25px auto 0;

          background:
            linear-gradient(
              90deg,
              var(--ignite-yellow),
              var(--ignite-red)
            );

          border-radius: 20px;
        }

        /* =========================================================
           MAIN SECTION
        ========================================================= */

        .ceo-main {
          position: relative;

          width: 100%;

          padding:
            90px 25px 100px;

          background:
            #ffffff;
        }

        .ceo-container {
          position: relative;

          z-index: 2;

          width:
            min(1180px, 100%);

          margin: 0 auto;

          display: grid;

          grid-template-columns:
            370px minmax(0, 1fr);

          gap: 55px;

          align-items: start;
        }

        /* =========================================================
           PROFILE
        ========================================================= */

        .ceo-profile {
          position: sticky;

          top: 110px;

          padding:
            0;

          background:
            var(--ignite-white);
        }

        .ceo-image-card {
          position: relative;

          width: 100%;

          overflow: hidden;

          border-radius:
            var(--ignite-radius);

          background:
            var(--ignite-green);

          box-shadow:
            var(--ignite-shadow);
        }

        .ceo-image-card::before {
          content: "";

          position: absolute;

          z-index: 2;

          left: 0;
          right: 0;
          bottom: 0;

          height: 35%;

          background:
            linear-gradient(
              to top,
              rgba(0, 0, 0, 0.58),
              transparent
            );

          pointer-events: none;
        }

        .ceo-image {
          width: 100%;

          height: 430px;

          display: block;

          object-fit: cover;

          object-position: center top;

          transition:
            transform 0.6s ease;
        }

        .ceo-image-card:hover .ceo-image {
          transform:
            scale(1.04);
        }

        .ceo-role-badge {
          position: absolute;

          z-index: 5;

          left: 22px;
          right: 22px;
          bottom: 20px;

          min-height: 58px;

          display: flex;
          align-items: center;

          gap: 12px;

          padding:
            10px 15px;

          color:
            var(--ignite-black);

          background:
            var(--ignite-yellow);

          border-radius: 10px;

          box-shadow:
            0 8px 20px
            rgba(0, 0, 0, 0.18);

          font-size: 12px;

          font-weight: 800;

          text-transform: uppercase;

          letter-spacing: 0.7px;
        }

        .ceo-role-badge svg {
          width: 31px;
          height: 31px;

          padding: 7px;

          flex-shrink: 0;

          color:
            var(--ignite-white);

          background:
            var(--ignite-green);

          border-radius: 50%;
        }

        .ceo-profile-info {
          margin-top: 20px;

          padding:
            25px 24px;

          background:
            var(--ignite-gray-light);

          border:
            1px solid
            var(--ignite-border);

          border-radius:
            var(--ignite-radius);
        }

        .ceo-profile-info .small-label {
          margin: 0 0 7px;

          color:
            var(--ignite-red);

          font-size: 10px;

          font-weight: 800;

          text-transform: uppercase;

          letter-spacing: 1.5px;
        }

        .ceo-profile-info h3 {
          margin: 0;

          color:
            var(--ignite-green-deep);

          font-size: 22px;

          font-weight: 900;

          line-height: 1.2;
        }

        .ceo-profile-info p {
          margin:
            8px 0 0;

          color:
            var(--ignite-gray);

          font-size: 12px;

          line-height: 1.6;
        }

        /* =========================================================
           SOCIALS
        ========================================================= */

        .ceo-socials {
          display: flex;

          gap: 8px;

          margin-top: 18px;
        }

        .ceo-socials a {
          width: 38px;
          height: 38px;

          display: flex;
          align-items: center;
          justify-content: center;

          color:
            var(--ignite-green);

          background:
            var(--ignite-white);

          border:
            1px solid
            var(--ignite-border);

          border-radius: 50%;

          text-decoration: none;

          transition:
            color 0.25s ease,
            background 0.25s ease,
            border-color 0.25s ease,
            transform 0.25s ease;
        }

        .ceo-socials a:hover {
          color:
            var(--ignite-black);

          background:
            var(--ignite-yellow);

          border-color:
            var(--ignite-yellow);

          transform:
            translateY(-3px);
        }

        /* =========================================================
           MESSAGE CONTENT
        ========================================================= */

        .ceo-message {
          position: relative;

          padding:
            48px 48px 45px;

          background:
            var(--ignite-white);

          border:
            1px solid
            var(--ignite-border);

          border-radius:
            var(--ignite-radius);

          box-shadow:
            0 15px 50px
            rgba(0, 0, 0, 0.065);
        }

        .message-top-line {
          width: 70px;
          height: 5px;

          margin-bottom: 23px;

          background:
            linear-gradient(
              90deg,
              var(--ignite-green),
              var(--ignite-yellow),
              var(--ignite-red)
            );

          border-radius: 20px;
        }

        .quote-icon {
          position: absolute;

          top: 28px;
          right: 35px;

          width: 58px;
          height: 58px;

          display: flex;
          align-items: center;
          justify-content: center;

          color:
            var(--ignite-green);

          background:
            var(--ignite-green-soft);

          border-radius: 50%;

          font-size: 23px;
        }

        .ceo-message .message-label {
          margin: 0 0 8px;

          color:
            var(--ignite-red);

          font-size: 10px;

          font-weight: 800;

          text-transform: uppercase;

          letter-spacing: 1.7px;
        }

        .ceo-message h2 {
          max-width: 620px;

          margin:
            0 0 25px;

          color:
            var(--ignite-green-deep);

          font-size:
            clamp(28px, 4vw, 43px);

          font-weight: 900;

          line-height: 1.1;

          letter-spacing: -1px;
        }

        .message-intro {
          margin:
            0 0 22px;

          color:
            var(--ignite-green-dark);

          font-size: 17px;

          font-weight: 700;

          line-height: 1.7;
        }

        .message-paragraph {
          margin:
            0 0 20px;

          color:
            #555d60;

          font-size: 14px;

          line-height: 1.9;
        }

        .message-paragraph strong {
          color:
            var(--ignite-green-deep);

          font-weight: 800;
        }

        /* =========================================================
           CORE VALUES
        ========================================================= */

        .ceo-focus-grid {
          margin-top: 35px;

          display: grid;

          grid-template-columns:
            repeat(3, 1fr);

          gap: 12px;
        }

        .focus-item {
          min-height: 135px;

          padding:
            20px 17px;

          background:
            #f9fbfa;

          border:
            1px solid
            var(--ignite-border);

          border-radius: 13px;

          transition:
            transform 0.25s ease,
            border-color 0.25s ease,
            box-shadow 0.25s ease;
        }

        .focus-item:hover {
          transform:
            translateY(-4px);

          border-color:
            rgba(8, 127, 35, 0.25);

          box-shadow:
            0 10px 25px
            rgba(0, 0, 0, 0.06);
        }

        .focus-icon {
          width: 38px;
          height: 38px;

          display: flex;
          align-items: center;
          justify-content: center;

          margin-bottom: 12px;

          color:
            var(--ignite-green);

          background:
            var(--ignite-green-soft);

          border-radius: 9px;

          font-size: 16px;
        }

        .focus-item:nth-child(2)
          .focus-icon {
          color:
            var(--ignite-red);

          background:
            rgba(217, 4, 41, 0.07);
        }

        .focus-item:nth-child(3)
          .focus-icon {
          color:
            var(--ignite-yellow-dark);

          background:
            rgba(244, 196, 0, 0.13);
        }

        .focus-item h4 {
          margin:
            0 0 5px;

          color:
            var(--ignite-black);

          font-size: 12px;

          font-weight: 800;

          text-transform: uppercase;

          letter-spacing: 0.3px;
        }

        .focus-item p {
          margin: 0;

          color:
            var(--ignite-gray);

          font-size: 10px;

          line-height: 1.55;
        }

        /* =========================================================
           CLOSING
        ========================================================= */

        .ceo-closing {
          margin-top: 35px;

          padding:
            27px 28px;

          background:
            linear-gradient(
              135deg,
              var(--ignite-green-deep),
              var(--ignite-green)
            );

          border-radius: 14px;

          position: relative;

          overflow: hidden;
        }

        .ceo-closing::after {
          content: "";

          position: absolute;

          width: 150px;
          height: 150px;

          right: -60px;
          top: -75px;

          border-radius: 50%;

          background:
            rgba(244, 196, 0, 0.14);
        }

        .ceo-closing p {
          position: relative;

          z-index: 2;

          margin: 0;

          color:
            rgba(255, 255, 255, 0.94);

          font-size: 14px;

          line-height: 1.8;

          font-weight: 500;
        }

        .ceo-signature {
          margin-top: 25px;

          display: flex;
          flex-direction: column;

          gap: 4px;
        }

        .signature-name {
          color:
            var(--ignite-green-deep);

          font-size: 18px;

          font-weight: 900;
        }

        .signature-title {
          color:
            var(--ignite-red);

          font-size: 10px;

          font-weight: 800;

          text-transform: uppercase;

          letter-spacing: 1.3px;
        }

        /* =========================================================
           CTA
        ========================================================= */

        .ceo-cta {
          position: relative;

          width:
            min(1180px, calc(100% - 50px));

          margin:
            0 auto 90px;

          padding:
            45px 50px;

          display: flex;
          align-items: center;
          justify-content: space-between;

          gap: 30px;

          overflow: hidden;

          background:
            var(--ignite-black);

          border-radius:
            var(--ignite-radius);
        }

        .ceo-cta::before {
          content: "";

          position: absolute;

          width: 280px;
          height: 280px;

          right: -100px;
          top: -150px;

          border-radius: 50%;

          border:
            55px solid
            rgba(8, 127, 35, 0.25);
        }

        .ceo-cta-content {
          position: relative;

          z-index: 2;
        }

        .ceo-cta-content small {
          color:
            var(--ignite-yellow);

          font-size: 10px;

          font-weight: 800;

          text-transform: uppercase;

          letter-spacing: 1.7px;
        }

        .ceo-cta-content h3 {
          margin:
            7px 0 0;

          color:
            var(--ignite-white);

          font-size:
            clamp(23px, 3vw, 34px);

          font-weight: 900;

          line-height: 1.2;
        }

        .ceo-cta-content p {
          max-width: 650px;

          margin:
            10px 0 0;

          color:
            rgba(255, 255, 255, 0.65);

          font-size: 13px;

          line-height: 1.7;
        }

        .ceo-cta-button {
          position: relative;

          z-index: 2;

          flex-shrink: 0;

          min-height: 48px;

          padding:
            0 22px;

          display: inline-flex;
          align-items: center;
          justify-content: center;

          gap: 9px;

          color:
            var(--ignite-black);

          background:
            var(--ignite-yellow);

          border:
            1px solid
            var(--ignite-yellow);

          border-radius: 8px;

          font-size: 12px;

          font-weight: 800;

          text-decoration: none;

          transition:
            transform 0.25s ease,
            background 0.25s ease,
            box-shadow 0.25s ease;
        }

        .ceo-cta-button:hover {
          color:
            var(--ignite-black);

          background:
            var(--ignite-yellow-light);

          transform:
            translateY(-3px);

          box-shadow:
            0 10px 25px
            rgba(244, 196, 0, 0.22);
        }

        /* =========================================================
           RESPONSIVE
        ========================================================= */

        @media (max-width: 1050px) {

          .ceo-container {
            grid-template-columns:
              320px minmax(0, 1fr);

            gap: 35px;
          }

          .ceo-image {
            height: 390px;
          }

          .ceo-message {
            padding:
              38px 35px;
          }

          .ceo-focus-grid {
            grid-template-columns:
              1fr;
          }
        }

        @media (max-width: 850px) {

          .ceo-hero {
            min-height: 350px;

            padding:
              80px 22px 65px;
          }

          .ceo-main {
            padding:
              65px 20px 75px;
          }

          .ceo-container {
            grid-template-columns:
              1fr;

            gap: 35px;
          }

          .ceo-profile {
            position: relative;

            top: auto;

            width:
              min(500px, 100%);

            margin:
              0 auto;
          }

          .ceo-image {
            height: 470px;
          }

          .ceo-focus-grid {
            grid-template-columns:
              repeat(3, 1fr);
          }

          .ceo-cta {
            width:
              calc(100% - 40px);

            margin-bottom: 70px;

            padding:
              38px 30px;

            flex-direction: column;

            align-items: flex-start;
          }
        }

        @media (max-width: 600px) {

          .ceo-hero {
            min-height: 320px;

            padding:
              70px 18px 55px;
          }

          .ceo-hero-label {
            padding:
              8px 13px;

            font-size: 8px;

            letter-spacing: 1.3px;
          }

          .ceo-hero-description {
            font-size: 13px;

            line-height: 1.7;
          }

          .ceo-main {
            padding:
              50px 15px 60px;
          }

          .ceo-image {
            height: 390px;
          }

          .ceo-profile-info {
            padding:
              21px 18px;
          }

          .ceo-message {
            padding:
              30px 21px;
          }

          .quote-icon {
            position: relative;

            top: auto;
            right: auto;

            margin-bottom: 18px;
          }

          .ceo-message h2 {
            font-size: 29px;

            letter-spacing: -0.7px;
          }

          .message-intro {
            font-size: 15px;
          }

          .message-paragraph {
            font-size: 13px;

            line-height: 1.8;
          }

          .ceo-focus-grid {
            grid-template-columns:
              1fr;
          }

          .focus-item {
            min-height: auto;
          }

          .ceo-closing {
            padding:
              23px 20px;
          }

          .ceo-cta {
            width:
              calc(100% - 30px);

            margin-bottom: 55px;

            padding:
              30px 22px;
          }

          .ceo-cta-button {
            width: 100%;
          }
        }

        @media (max-width: 390px) {

          .ceo-image {
            height: 350px;
          }

          .ceo-role-badge {
            left: 13px;
            right: 13px;
            bottom: 13px;

            font-size: 10px;
          }

          .ceo-message h2 {
            font-size: 26px;
          }

          .ceo-signature {
            margin-top: 20px;
          }
        }

        /* =========================================================
           ACCESSIBILITY
        ========================================================= */

        .ceo-socials a:focus-visible,
        .ceo-cta-button:focus-visible {
          outline:
            3px solid
            var(--ignite-yellow);

          outline-offset: 3px;
        }

        /* =========================================================
           REDUCED MOTION
        ========================================================= */

        @media (prefers-reduced-motion: reduce) {

          .ceo-image,
          .ceo-socials a,
          .focus-item,
          .ceo-cta-button {

            transition: none !important;
          }
        }
      `}</style>

      {/* =========================================================
          HERO
      ========================================================= */}

      <section className="ceo-hero">

        <div className="ceo-hero-inner">

          <motion.div
            className="ceo-hero-label"
            initial={{
              opacity: 0,
              y: 20,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.6,
            }}
          >
            <FaBriefcase />
            Leadership & Vision
          </motion.div>

          <motion.h1
            initial={{
              opacity: 0,
              y: 25,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.7,
              delay: 0.1,
            }}
          >
            Message From Our{" "}
            <span>CEO</span>
          </motion.h1>

          <motion.p
            className="ceo-hero-description"
            initial={{
              opacity: 0,
              y: 20,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.7,
              delay: 0.2,
            }}
          >
            A message of purpose, practical training, opportunity,
            innovation, and commitment to building skilled and
            productive professionals.
          </motion.p>

          <motion.div
            className="hero-accent"
            initial={{
              width: 0,
              opacity: 0,
            }}
            animate={{
              width: 80,
              opacity: 1,
            }}
            transition={{
              duration: 0.6,
              delay: 0.35,
            }}
          />

        </div>

      </section>

      {/* =========================================================
          MAIN CONTENT
      ========================================================= */}

      <section className="ceo-main">

        <div className="ceo-container">

          {/* =====================================================
              CEO PROFILE
          ===================================================== */}

          <motion.div
            className="ceo-profile"
            initial={{
              opacity: 0,
              x: -70,
            }}
            whileInView={{
              opacity: 1,
              x: 0,
            }}
            transition={{
              duration: 0.8,
            }}
            viewport={{
              once: true,
              amount: 0.15,
            }}
          >

            <div className="ceo-image-card">

              <img
                src={ceoImage}
                alt="CEO of Ignite S Vocational School"
                className="ceo-image"
              />

              <div className="ceo-role-badge">

                <FaGraduationCap />

                <span>
                  Chief Executive Officer
                </span>

              </div>

            </div>

            <div className="ceo-profile-info">

              <p className="small-label">
                Ignite S Skill Center
              </p>

              <h3>
                {/* Leadership With Purpose */}
                Sekou Sumawolo - Founder
              </h3>

              <p>
                Committed to practical education,
                professional development, innovation,
                employment, and entrepreneurship.
              </p>

              <div className="ceo-socials">

                <a
                  href="#"
                  aria-label="Facebook"
                >
                  <FaFacebookF />
                </a>

                <a
                  href="mailto:info@ignitesvocational.edu"
                  aria-label="Email CEO"
                >
                  <FaEnvelope />
                </a>

              </div>

            </div>

          </motion.div>

          {/* =====================================================
              MESSAGE
          ===================================================== */}

          <motion.div
            className="ceo-message"
            initial={{
              opacity: 0,
              x: 70,
            }}
            whileInView={{
              opacity: 1,
              x: 0,
            }}
            transition={{
              duration: 0.8,
            }}
            viewport={{
              once: true,
              amount: 0.15,
            }}
          >

            <div className="quote-icon">
              <FaQuoteLeft />
            </div>

            <div className="message-top-line" />

            <p className="message-label">
              A Message From Our CEO
            </p>

            <h2>
              Skills Today.
              <br />
              Opportunities Tomorrow.
            </h2>

            <p className="message-intro">
              Welcome to Ignite S Skill Center, where
              education is connected directly to practical
              skills, meaningful work, entrepreneurship, and
              the changing needs of the modern workforce.
            </p>

<p className="message-paragraph">
    Whether you are a student beginning your journey, a teacher shaping
    young minds, or a visitor simply passing through our website — I want
    you to know what this school means to me, and why it exists.

    IgniteS was born from a simple belief: that a practical skill in your
    hands is one of the most powerful things a person can own. Here in Monrovia, Liberia, 
    I have watched too many bright,
    hardworking young people struggle simply because no one gave them the
    chance to learn a trade. We started this school to change that.
</p>

<p className="message-paragraph">
    Our passion is <strong>hands-on vocational training</strong> — real skills,
    taught with discipline and care, that lead to <strong>real work</strong>.
    When a young woman learns tailoring, when a young man masters decoration,
    when a student leaves our doors able to <strong>earn a living with their
    own hands</strong> — that is not just one life changed. It is a family fed,
    a community strengthened, and a nation built from the ground up.
</p>

<p className="message-paragraph">
    We are not just training workers. We are raising
    <strong> self-reliant men and women</strong> — people who can stand on
    their own, <strong>create jobs for others</strong>, and lift the
    communities around them. That is the future we are working toward,
    <strong> one student at a time.</strong>
</p>

            <p className="message-paragraph">
With gratitude,
*Sekou Sumawolo*
CEO, IgniteS Skill Center
New Life Community, Johnsonville, Montserrado County, Republic of Liberia
            </p>




            {/* =================================================
                FOCUS AREAS
            ================================================= */}

            <div className="ceo-focus-grid">

              <div className="focus-item">

                <div className="focus-icon">
                  <FaTools />
                </div>

                <h4>
                  Practical Skills
                </h4>

                <p>
                  Learning through hands-on training,
                  projects, tools, and real-world practice.
                </p>

              </div>

              <div className="focus-item">

                <div className="focus-icon">
                  <FaBriefcase />
                </div>

                <h4>
                  Career Readiness
                </h4>

                <p>
                  Preparing learners with professional
                  habits and workplace-ready skills.
                </p>

              </div>

              <div className="focus-item">

                <div className="focus-icon">
                  <FaLightbulb />
                </div>

                <h4>
                  Innovation
                </h4>

                <p>
                  Encouraging creativity, technology,
                  problem-solving, and new ideas.
                </p>

              </div>

            </div>

            {/* =================================================
                CLOSING MESSAGE
            ================================================= */}

            <div className="ceo-closing">

              <p>
                Our vision is to develop skilled individuals
                who can contribute positively to their families,
                communities, businesses, and the wider economy.
                We invite parents, students, partners, employers,
                and community members to join us as we build a
                stronger culture of skills, work, innovation,
                and entrepreneurship.
              </p>

            </div>

            <div className="ceo-signature">

              <span className="signature-name">
                Chief Executive Officer
              </span>

              <span className="signature-title">
                Ignite S Skill Center
              </span>

            </div>

          </motion.div>

        </div>

      </section>

      {/* =========================================================
          CTA
      ========================================================= */}

      <motion.section
        className="ceo-cta"
        initial={{
          opacity: 0,
          y: 35,
        }}
        whileInView={{
          opacity: 1,
          y: 0,
        }}
        transition={{
          duration: 0.7,
        }}
        viewport={{
          once: true,
          amount: 0.2,
        }}
      >

        <div className="ceo-cta-content">

          <small>
            Ignite S Vocational School
          </small>

          <h3>
            Learn a Skill. Build a Career.
          </h3>

          <p>
            Practical training designed to help learners
            develop useful skills, discover opportunities,
            and prepare for productive careers and
            entrepreneurship.
          </p>

        </div>

        <a
          href="/login"
          className="ceo-cta-button"
        >
          Get Started
          <FaArrowRight />
        </a>

      </motion.section>

      <Footer />

    </div>
  );
};

export default PrincipalMessagePage;